import React from 'react';
import { motion } from 'framer-motion';
import { Coffee, Users, ChefHat, ArrowUpRight } from 'lucide-react';

const SERVICES = [
  {
    id: 'cafe',
    number: '01',
    name: 'Vibe Café',
    icon: Coffee,
    description: 'Artisanal coffee, fresh pastries, and in-house baked goods. Open Monday through Saturday — come for the coffee, stay for the vibe.',
    features: ['Specialty Coffee & Lattes', 'In-House Baked Goods', 'Local Art Displays', 'Grab & Go'],
    image: 'https://static.wixstatic.com/media/436483e6ed9e41fe91b9f286d2ea4efb.jpg/v1/fill/w_1200,h_900,al_c,q_85,enc_avif,quality_auto/436483e6ed9e41fe91b9f286d2ea4efb.jpg'
  },
  {
    id: 'workspace',
    number: '02',
    name: 'Shared Workspace',
    icon: Users,
    description: 'Professional meeting rooms and collaborative space for entrepreneurs, remote workers, and creatives — with conference technology on tap.',
    features: ['High-Speed WiFi', 'Conference Rooms', 'Meeting Rentals', 'Work From The Vibe Days'],
    image: 'https://customer-assets.emergentagent.com/job_pottstown-vibe-hub/artifacts/2fyaqe3g_vibe%20picture%20.JPG'
  },
  {
    id: 'kitchen',
    number: '03',
    name: 'Commercial Kitchen',
    icon: ChefHat,
    description: 'A fully-equipped, commercial-grade kitchen available to rent for catering, pop-ups, and food entrepreneurs building their business.',
    features: ['Commercial Appliances', 'Food Prep Space', 'Storage Areas', 'Health Dept Certified'],
    image: 'https://static.wixstatic.com/media/720c8e_ad5f617c71374a7aaffc9be9f534a03f~mv2.png/v1/fill/w_1200,h_1200,al_c,q_85,enc_avif,quality_auto/720c8e_ad5f617c71374a7aaffc9be9f534a03f~mv2.png'
  }
];

const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(`#${id}`, { offset: -72 });
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }
};

const ServiceChapter = ({ service, index }) => {
  const Icon = service.icon;
  const reversed = index % 2 === 1;

  return (
    <motion.article
      initial={{ opacity: 0, y: 70 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`grid items-center gap-10 border-t border-[#14100d]/15 py-16 sm:py-20 lg:grid-cols-2 lg:gap-16 ${
        reversed ? '' : ''
      }`}
      data-testid={`service-chapter-${service.id}`}
    >
      {/* Text */}
      <div className={reversed ? 'lg:order-2' : ''}>
        <div className="flex items-baseline gap-5">
          <span className="chapter-number text-5xl sm:text-6xl" style={{ WebkitTextStroke: '1px rgba(20,16,13,0.5)' }}>
            {service.number}
          </span>
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#14100d]">
            <Icon size={20} className="text-[#f2a33c]" />
          </div>
        </div>
        <h3 className="font-display mt-6 text-[clamp(2.2rem,5vw,3.5rem)] font-semibold leading-[1.02] tracking-tight text-[#14100d]">
          {service.name}
        </h3>
        <p className="font-body mt-5 max-w-lg text-base leading-relaxed text-[#4d453c] sm:text-lg">
          {service.description}
        </p>
        <ul className="mt-7 grid max-w-md grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          {service.features.map((feature) => (
            <li key={feature} className="font-body flex items-center gap-2 text-sm text-[#4d453c]">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#c96f1e]" />
              {feature}
            </li>
          ))}
        </ul>
        <button
          data-testid={`service-book-${service.id}`}
          onClick={() => scrollToId('booking')}
          className="font-body group mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#14100d] transition-colors hover:text-[#c96f1e]"
        >
          Rent this space
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </button>
      </div>

      {/* Spotlight image */}
      <motion.div
        initial={{ clipPath: 'inset(8% 8% 8% 8%)', opacity: 0.6 }}
        whileInView={{ clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className={`frame-spotlight overflow-hidden rounded-2xl ${reversed ? 'lg:order-1' : ''}`}
      >
        <img
          src={service.image}
          alt={service.name}
          loading="lazy"
          className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
        />
      </motion.div>
    </motion.article>
  );
};

const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="bg-[#f4ede3] px-6 py-24 sm:px-12 sm:py-32">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-body text-[11px] font-medium uppercase tracking-[0.45em] text-[#c96f1e]">
            The Spaces
          </p>
          <h2 className="font-display mt-4 max-w-3xl text-[clamp(2.6rem,6vw,4.5rem)] font-semibold leading-[1.02] tracking-tight text-[#14100d]">
            Three ways to make it <span className="italic text-[#c96f1e]">yours</span>
          </h2>
        </motion.div>

        <div className="mt-10">
          {SERVICES.map((service, index) => (
            <ServiceChapter key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
